import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Home,
  BarChart2,
  FileText,
  Settings,
  Users,
  Bell,
  ClipboardCheck,
  Filter,
  Search,
  Plus,
  Activity,
  Calendar,
  Clock,
  AlertTriangle,
  LayoutGrid,
  List,
  Target,
  ChevronDown,
  Building2,
  ChevronRight,
  Star,
  CheckCircle,
  XCircle,
  AlertCircle,
  PieChart,
  TrendingUp,
  ChevronUp,
  ArrowUpRight,
  ArrowDownRight,
  X,
  ExternalLink,
  Camera,
  Menu
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer, 
  BarChart, 
  Bar,
  Cell,
  Pie,
  PieChart as RePieChart,
  RadialBarChart,
  RadialBar,
  Area,
  AreaChart
} from 'recharts';

const GlassCard = ({ children, className = '' }) => (
  <div className={`bg-white/60 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 ${className}`}>
    {children}
  </div>
);

const NavButton = ({ active, icon: Icon, children, onClick, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-200 ${
      active 
        ? 'bg-indigo-600 text-white shadow-lg' 
        : 'text-gray-600 hover:bg-indigo-50 hover:text-indigo-600'
    }`}
  >
    <Icon className="w-5 h-5 mr-2" />
    <span>{children}</span>
    {count !== undefined && (
      <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
        active 
          ? 'bg-white text-indigo-600' 
          : 'bg-gray-100 text-gray-600'
      }`}>
        {count}
      </span>
    )}
  </button>
);

const AnimatedBackground = () => (
  <div className="fixed inset-0 -z-10">
    <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.8),transparent)] animate-pulse" />
    </div>
  </div>
);

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        ></div>

        <div className="relative transform overflow-hidden rounded-2xl bg-white text-left shadow-xl transition-all sm:my-8 w-full sm:max-w-3xl">
          <div className="px-4 pt-5 pb-4 sm:p-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">{title}</h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="mt-2">{children}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatusBadge = ({ score }) => {
  const getStatus = (score) => {
    if (score >= 90) return { 
      label: 'Excellent', 
      color: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
      icon: CheckCircle
    };
    if (score >= 80) return { 
      label: 'Good', 
      color: 'bg-blue-100 text-blue-700 border border-blue-200',
      icon: Star
    };
    if (score >= 70) return { 
      label: 'Fair', 
      color: 'bg-amber-100 text-amber-700 border border-amber-200',
      icon: AlertCircle
    };
    return { 
      label: 'Needs Improvement', 
      color: 'bg-rose-100 text-rose-700 border border-rose-200',
      icon: XCircle
    };
  };

  const status = getStatus(score);
  const StatusIcon = status.icon;

  return (
    <span className={`px-3 py-1 rounded-full text-xs font-medium ${status.color} flex items-center gap-1`}>
      <StatusIcon className="w-3 h-3" />
      {status.label}
    </span>
  );
};

const TrendIndicator = ({ value, className = '' }) => {
  const isPositive = value >= 0;
  return (
    <div className={`flex items-center gap-1 text-sm ${
      isPositive ? 'text-emerald-600' : 'text-rose-600'
    } ${className}`}>
      {isPositive ? (
        <ArrowUpRight className="w-4 h-4" />
      ) : (
        <ArrowDownRight className="w-4 h-4" />
      )}
      <span>{Math.abs(value)}%</span>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white p-3 rounded-lg shadow-lg border border-gray-200">
        <p className="font-medium text-gray-900">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
            {entry.unit || '%'}
          </p>
        ))}
      </div>
    );
  }
  return null;
};
const MobileNavigation = ({ activeTab, setActiveTab, navItems }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      {/* Main Navigation */}
      <div className="fixed bottom-0 left-0 right-0 z-50 md:hidden">
        <div className="bg-white border-t border-gray-200">
          <div className="max-w-md mx-auto px-6 py-2">
            <div className="flex justify-between items-center">
              {navItems.slice(0, 4).map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                
                return (
                  <button
                    key={item.id}
                    onClick={() => setActiveTab(item.id)}
                    className="flex flex-col items-center py-2 px-3"
                  >
                    <div className="relative">
                      <Icon className={`w-6 h-6 ${isActive ? 'text-indigo-600' : 'text-gray-500'}`} />
                      {item.count !== undefined && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 text-[10px] font-bold bg-red-500 text-white rounded-full flex items-center justify-center">
                          {item.count}
                        </span>
                      )}
                    </div>
                    <span className={`mt-1 text-[10px] font-medium ${
                      isActive ? 'text-indigo-600' : 'text-gray-500'
                    }`}>
                      {item.label}
                    </span>
                    {isActive && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-indigo-600" />
                    )}
                  </button>
                );
              })}
              
              <button
                onClick={() => setIsMenuOpen(true)}
                className="flex flex-col items-center py-2 px-3"
              >
                <Menu className="w-6 h-6 text-gray-500" />
                <span className="mt-1 text-[10px] font-medium text-gray-500">
                  Menu
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Menu Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-50 md:hidden bg-black/50">
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-xl">
            <div className="p-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">More Options</h3>
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = activeTab === item.id;
                  
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        setActiveTab(item.id);
                        setIsMenuOpen(false);
                      }}
                      className={`w-full flex items-center px-4 py-3 rounded-lg ${
                        isActive ? 'text-indigo-600 bg-indigo-50' : 'text-gray-700'
                      }`}
                    >
                      <Icon className="w-5 h-5 mr-3" />
                      <span className="font-medium">{item.label}</span>
                      {item.count !== undefined && (
                        <span className={`ml-auto px-2 py-0.5 text-xs rounded-full ${
                          isActive 
                            ? 'bg-indigo-100 text-indigo-600' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {item.count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const MobileFloatingButton = ({ onClick }) => (
  <button
    onClick={onClick}
    className="fixed bottom-20 right-4 z-40 p-4 bg-indigo-600 text-white rounded-full shadow-lg md:hidden hover:bg-indigo-700 transition-colors"
  >
    <Plus className="w-6 h-6" />
  </button>
);

const generateMockData = () => {
  const branches = [
    'Olaya Store', 'Hafr Albatin', 'Alahsa Mall', 'Riyadh Park',
    'Red Sea Mall', 'Hayat Mall', 'Panorama Mall', 'Nakheel Mall',
    'Granada Center', 'Hamra Mall', 'Jubail Galleria', 'Dammam Park'
  ];

  const generateRandomScore = () => Math.floor(Math.random() * 31) + 70;
  const generateRandomTrend = () => (Math.random() * 20 - 10).toFixed(1);

  const generateAuditSections = () => ({
    foodSafety: {
      score: generateRandomScore(),
      findings: Math.floor(Math.random() * 4),
      critical: Math.floor(Math.random() * 2),
      items: [
        { name: 'Temperature Control', score: generateRandomScore() },
        { name: 'Storage Practices', score: generateRandomScore() },
        { name: 'Personal Hygiene', score: generateRandomScore() },
        { name: 'Cross Contamination', score: generateRandomScore() }
      ]
    },
    customerService: {
      score: generateRandomScore(),
      findings: Math.floor(Math.random() * 3),
      items: [
        { name: 'Staff Courtesy', score: generateRandomScore() },
        { name: 'Order Accuracy', score: generateRandomScore() },
        { name: 'Wait Times', score: generateRandomScore() }
      ]
    },
    cleanliness: {
      score: generateRandomScore(),
      findings: Math.floor(Math.random() * 3),
      items: [
        { name: 'Front of House', score: generateRandomScore() },
        { name: 'Kitchen Area', score: generateRandomScore() },
        { name: 'Washrooms', score: generateRandomScore() },
        { name: 'External Areas', score: generateRandomScore() }
      ]
    },
    operations: {
      score: generateRandomScore(),
      findings: Math.floor(Math.random() * 4),
      items: [
        { name: 'Documentation', score: generateRandomScore() },
        { name: 'Staff Training', score: generateRandomScore() },
        { name: 'Equipment Maintenance', score: generateRandomScore() }
      ]
    }
  });

  return branches.map((branch, index) => {
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 90));
    
    const sections = generateAuditSections();
    const sectionScores = Object.values(sections).map(s => s.score);
    const overallScore = Math.round(
      sectionScores.reduce((a, b) => a + b, 0) / sectionScores.length
    );

    const findings = Object.values(sections).reduce(
      (acc, section) => acc + (section.findings || 0),
      0
    );

    const criticalFindings = Object.values(sections).reduce(
      (acc, section) => acc + (section.critical || 0),
      0
    );

    const historicalScores = Array.from({ length: 6 }, () => 
      Math.floor(Math.random() * 15) + 80
    );

    return {
      id: index + 1,
      date: date.toISOString(),
      branch,
      score: overallScore,
      trend: generateRandomTrend(),
      sections,
      auditor: `Auditor ${(index % 5) + 1}`,
      status: overallScore >= 90 ? 'Excellent' : 
              overallScore >= 80 ? 'Good' :
              overallScore >= 70 ? 'Fair' : 'Needs Improvement',
      findings,
      criticalFindings,
      historicalScores,
      notes: [
        'Temperature logs incomplete',
        'Staff training certificates updated',
        'New cleaning checklist implemented',
        'Equipment maintenance schedule updated'
      ].slice(0, Math.floor(Math.random() * 3) + 1),
      actionItems: [
        'Update HACCP documentation',
        'Schedule staff refresher training',
        'Replace damaged equipment',
        'Implement new cleaning schedule'
      ].slice(0, Math.floor(Math.random() * 3) + 1),
      photos: Array(Math.floor(Math.random() * 4) + 1).fill(null).map((_, i) => ({
        id: i + 1,
        url: `/api/placeholder/400/300`,
        caption: `Audit photo ${i + 1}`
      }))
    };
  });
};

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const calculateTrend = (current, previous) => {
  if (!previous) return 0;
  return ((current - previous) / previous * 100).toFixed(1);
};
const AuditDetailsModal = ({ audit, isOpen, onClose }) => {
  if (!audit) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Audit Details - ${audit.branch}`}
    >
      <div className="space-y-6">
        {/* Header Info */}
        <div className="flex justify-between items-start">
          <div>
            <h4 className="text-lg font-medium text-gray-900">{formatDate(audit.date)}</h4>
            <p className="text-sm text-gray-500">Conducted by {audit.auditor}</p>
          </div>
          <StatusBadge score={audit.score} />
        </div>

        {/* Overall Score */}
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Overall Score</span>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">{audit.score}%</span>
              <TrendIndicator value={audit.trend} />
            </div>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="h-2.5 rounded-full transition-all duration-300"
              style={{
                width: `${audit.score}%`,
                backgroundColor: `${
                  audit.score >= 90 ? '#10B981' :
                  audit.score >= 80 ? '#3B82F6' :
                  audit.score >= 70 ? '#F59E0B' :
                  '#EF4444'
                }`
              }}
            />
          </div>
        </div>

        {/* Section Scores */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-4">Section Scores</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(audit.sections).map(([section, data]) => (
              <div key={section} className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-700">
                    {section.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                  <span className="text-sm font-semibold text-gray-900">{data.score}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div
                    className="h-1.5 rounded-full bg-indigo-500"
                    style={{ width: `${data.score}%` }}
                  />
                </div>
                {data.findings > 0 && (
                  <p className="mt-2 text-sm text-gray-500">
                    {data.findings} finding{data.findings !== 1 ? 's' : ''}
                    {data.critical > 0 && ` (${data.critical} critical)`}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Item Scores */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-4">Item Details</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(audit.sections).map(([section, data]) => (
              <div key={section}>
                <h5 className="text-sm font-medium text-gray-900 mb-2">
                  {section.replace(/([A-Z])/g, ' $1').trim()}
                </h5>
                <div className="space-y-2">
                  {data.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center">
                      <span className="text-sm text-gray-600">{item.name}</span>
                      <span className="text-sm font-medium">{item.score}%</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Findings and Notes */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {audit.actionItems?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Action Items</h4>
              <ul className="space-y-2">
                {audit.actionItems.map((item, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                    <ChevronRight className="w-4 h-4 mt-0.5 text-gray-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {audit.notes?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Notes</h4>
              <ul className="space-y-2">
                {audit.notes.map((note, index) => (
                  <li key={index} className="flex items-start gap-2 text-sm text-gray-600">
                    <ChevronRight className="w-4 h-4 mt-0.5 text-gray-400" />
                    {note}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Photos */}
        {audit.photos?.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-4">Photos</h4>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {audit.photos.map((photo) => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.url}
                    alt={photo.caption}
                    className="rounded-lg object-cover w-full h-32"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                    <Camera className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Historical Performance */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-4">Historical Performance</h4>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={audit.historicalScores.map((score, index) => ({
                month: `Month ${index + 1}`,
                score
              }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" />
                <YAxis domain={[60, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="#818CF8"
                  strokeWidth={2}
                  dot={{ r: 4, fill: '#818CF8' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex justify-end gap-4 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:text-gray-900"
          >
            Close
          </button>
          <Link
            to={`/audits/${audit.id}`}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            View Full Report
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </Modal>
  );
};

const AuditDashboard = () => {
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('Overview');
  const [viewMode, setViewMode] = useState('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [audits, setAudits] = useState([]);
  const [branchFilter, setBranchFilter] = useState('All');
  const [selectedAudit, setSelectedAudit] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState('7d');
  const [notificationCount, setNotificationCount] = useState(3);
  const [loading, setLoading] = useState(true);

  const navItems = [
    { id: 'Overview', icon: Home, label: 'Overview' },
    { id: 'Audits', icon: ClipboardCheck, label: 'Audits', count: 45 },
    { id: 'Reports', icon: FileText, label: 'Reports' },
    { id: 'Analytics', icon: BarChart2, label: 'Analytics' },
    { id: 'Teams', icon: Users, label: 'Teams' },
    { id: 'Notifications', icon: Bell, label: 'Notifications', count: notificationCount },
    { id: 'Settings', icon: Settings, label: 'Settings' }
  ];

  const timeRanges = [
    { id: '7d', label: '7 Days' },
    { id: '30d', label: '30 Days' },
    { id: '90d', label: '90 Days' },
    { id: 'custom', label: 'Custom Range' }
  ];

  const chartConfig = {
    height: {
      sm: 200,
      md: 300
    },
    margin: {
      sm: { top: 20, right: 20, left: 0, bottom: 20 },
      md: { top: 20, right: 30, left: 20, bottom: 20 }
    },
    colors: {
      primary: '#818CF8',
      secondary: '#E5E7EB',
      success: '#10B981',
      warning: '#F59E0B',
      danger: '#EF4444'
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const mockAudits = generateMockData();
        setAudits(mockAudits.sort((a, b) => new Date(b.date) - new Date(a.date)));
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  const filteredAudits = audits.filter(audit => {
    const matchesSearch = audit.branch.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        audit.auditor.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBranch = branchFilter === 'All' || audit.branch === branchFilter;
    return matchesSearch && matchesBranch;
  });

  const bestPerformingStores = [...audits]
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)
    .map(audit => ({
      name: audit.branch,
      score: audit.score,
      trend: audit.trend
    }));

  const worstPerformingStores = [...audits]
    .sort((a, b) => a.score - b.score)
    .slice(0, 5)
    .map(audit => ({
      name: audit.branch,
      score: audit.score,
      trend: audit.trend
    }));

  const handleStartNewAudit = () => {
    navigate('/audit/new');
  };

  const generateTrendData = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    let baseScore = 85;
    
    return months.map(month => {
      baseScore = Math.min(Math.max(
        baseScore + (Math.random() * 6 - 3),
        75
      ), 95);
      
      return {
        month,
        score: Math.round(baseScore),
        target: 90
      };
    });
  };

  const renderOverviewTab = () => (
    <>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <GlassCard className="p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-emerald-100 rounded-xl">
              <Activity className="w-8 h-8 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500">Average Score</p>
              <div className="flex items-center gap-2 mt-1">
                <h3 className="text-2xl font-bold text-gray-800">
                  {Math.round(audits.reduce((acc, curr) => acc + curr.score, 0) / audits.length || 0)}%
                </h3>
                <TrendIndicator value={2.5} className="text-xs" />
              </div>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Calendar className="w-8 h-8 text-blue-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500">Completed This Month</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">
                {audits.filter(a => new Date(a.date) > new Date(new Date().setDate(1))).length}
              </h3>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-amber-100 rounded-xl">
              <AlertTriangle className="w-8 h-8 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500">Critical Findings</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">
                {audits.reduce((acc, curr) => acc + curr.criticalFindings, 0)}
              </h3>
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-5">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-rose-100 rounded-xl">
              <Target className="w-8 h-8 text-rose-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm text-gray-500">Compliance Rate</p>
              <h3 className="text-2xl font-bold text-gray-800 mt-1">
                {Math.round(audits.filter(a => a.score >= 80).length / audits.length * 100)}%
              </h3>
            </div>
          </div>
        </GlassCard>
      </div>

      {/* Search and Filters */}
      <GlassCard className="mb-6">
        <div className="p-4 space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search by branch or auditor..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-white/50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={branchFilter}
              onChange={(e) => setBranchFilter(e.target.value)}
              className="flex-1 bg-white/50 border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="All">All Branches</option>
              {[...new Set(audits.map(a => a.branch))].map(branch => (
                <option key={branch} value={branch}>{branch}</option>
              ))}
            </select>
            
            <div className="flex gap-2 overflow-x-auto pb-2 sm:pb-0 sm:flex-wrap">
              {timeRanges.map(range => (
                <button
                  key={range.id}
                  onClick={() => setSelectedTimeRange(range.id)}
                  className={`whitespace-nowrap px-4 py-3 rounded-xl transition-colors ${
                    selectedTimeRange === range.id 
                      ? 'bg-indigo-600 text-white' 
                      : 'bg-white/50 text-gray-600 hover:bg-indigo-50'
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Trend Chart */}
        <GlassCard className="p-5">
          <h2 className="text-lg font-semibold mb-6">Audit Scores Trend</h2>
          <div className="h-[300px] sm:h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={generateTrendData()}>
                <defs>
                  <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#818CF8" stopOpacity={0.1}/>
                    <stop offset="95%" stopColor="#818CF8" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="month" />
                <YAxis domain={[60, 100]} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke="#818CF8"
                  strokeWidth={3}
                  fill="url(#scoreGradient)"
                  dot={{ r: 6, fill: '#818CF8' }}
                  activeDot={{ r: 8 }}
                />
                <Line
                  type="monotone"
                  dataKey="target"
                  stroke="#E5E7EB"
                  strokeWidth={2}
                  strokeDasharray="5 5"
                  dot={false}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        {/* Category Performance */}
        <GlassCard className="p-5">
          <h2 className="text-lg font-semibold mb-6">Category Performance</h2>
          <div className="h-[300px] sm:h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={Object.entries(filteredAudits[0]?.sections || {}).map(([category, data]) => ({
                  category: category.replace(/([A-Z])/g, ' $1').trim(),
                  score: data.score
                }))}
                margin={{ top: 5, right: 30, left: 20, bottom: 60 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="category" 
                  angle={-45}
                  textAnchor="end"
                  height={60}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip />
                <Bar 
                  dataKey="score" 
                  fill="#818CF8"
                  radius={[4, 4, 0, 0]}
                >
                  {Object.entries(filteredAudits[0]?.sections || {}).map((_, index) => (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={`hsl(${index * (360 / Object.keys(filteredAudits[0]?.sections || {}).length)}, 70%, 60%)`}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        {/* Best Performing */}
        <GlassCard className="p-5">
          <h2 className="text-lg font-semibold mb-6">Best Performing Locations</h2>
          <div className="h-[300px] sm:h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={bestPerformingStores}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip />
                <Bar 
                  dataKey="score" 
                  fill="#10B981"
                  radius={[0, 4, 4, 0]}
                  label={{ 
                    position: 'right',
                    formatter: (value) => `${value}%`
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>

        {/* Worst Performing */}
        <GlassCard className="p-5">
          <h2 className="text-lg font-semibold mb-6">Stores Requiring Improvement</h2>
          <div className="h-[300px] sm:h-[400px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={worstPerformingStores}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} />
                <YAxis dataKey="name" type="category" width={120} />
                <Tooltip />
                <Bar 
                  dataKey="score" 
                  fill="#EF4444"
                  radius={[0, 4, 4, 0]}
                  label={{ 
                    position: 'right',
                    formatter: (value) => `${value}%`
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      </div>

      {/* Recent Audits */}
      <GlassCard className="p-4 md:p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg md:text-xl font-semibold">Recent Audits</h2>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-500">
              Showing {filteredAudits.length} of {audits.length} audits
            </span>
            <div className="hidden md:flex gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' 
                  ? 'bg-indigo-100 text-indigo-600' 
                  : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <LayoutGrid size={20} />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' 
                  ? 'bg-indigo-100 text-indigo-600' 
                  : 'text-gray-500 hover:bg-gray-100'}`}
              >
                <List size={20} />
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'grid' || window.innerWidth < 768 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAudits.map((audit) => (
              <div 
                key={audit.id}
                className="bg-white/50 rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow duration-200"
              >
                <div className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="font-semibold text-gray-900">{audit.branch}</h3>
                      <p className="text-sm text-gray-500">{formatDate(audit.date)}</p>
                    </div>
                    <StatusBadge score={audit.score} />
                  </div>

                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-gray-500">Overall Score</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{audit.score}%</span>
                          <TrendIndicator value={audit.trend} />
                        </div>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div 
                          className="h-2 rounded-full transition-all duration-300"
                          style={{ 
                            width: `${audit.score}%`,
                            backgroundColor: `${
                              audit.score >= 90 ? '#10B981' :
                              audit.score >= 80 ? '#3B82F6' :
                              audit.score >= 70 ? '#F59E0B' :
                              '#EF4444'
                            }`
                          }}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Auditor</span>
                        <p className="font-medium mt-1">{audit.auditor}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Findings</span>
                        <p className="font-medium mt-1">
                          <span className="text-rose-600">{audit.criticalFindings}</span>
                          <span className="text-gray-500"> critical</span>
                        </p>
                      </div>
                    </div>

                    <div className="pt-4 flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setSelectedAudit(audit);
                          setIsModalOpen(true);
                        }}
                        className="px-4 py-2 text-indigo-600 border border-indigo-600 rounded-lg text-sm hover:bg-indigo-50 transition-colors"
                      >
                        View Details
                      </button>
                      <Link
                        to={`/audits/${audit.id}/report`}
                        className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
                      >
                        Full Report
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50/50">
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Branch</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Auditor</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Score</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Findings</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200/50">
                {filteredAudits.map((audit) => (
                  <tr key={audit.id} className="hover:bg-gray-50/30">
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{formatDate(audit.date)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{audit.branch}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">{audit.auditor}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{audit.score}%</span>
                        <TrendIndicator value={audit.trend} />
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge score={audit.score} />
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <span className="text-rose-600 font-medium">{audit.criticalFindings}</span>
                        <span className="text-gray-500"> critical, </span>
                        <span className="font-medium">{audit.findings}</span>
                        <span className="text-gray-500"> total</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedAudit(audit);
                            setIsModalOpen(true);
                          }}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                        >
                          View Details
                        </button>
                        <span className="text-gray-300">|</span>
                        <Link
                          to={`/audits/${audit.id}/report`}
                          className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                        >
                          Full Report
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>

      {/* Audit Details Modal */}
      <AuditDetailsModal
        audit={selectedAudit}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedAudit(null);
        }}
      />
    </>
  );

  return (
    <div className="min-h-screen bg-transparent pb-20 md:pb-0">
      <AnimatedBackground />
      
      <div className="p-4 md:p-6">
        {/* Header Section */}
        <div className="mb-6 md:mb-8">
          <div className="flex justify-between items-center mb-4 md:mb-6">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-gray-800 mb-1 md:mb-2">
                Audit Dashboard
              </h1>
              <p className="text-sm md:text-base text-gray-500">
                Monitoring quality standards across {audits.length} locations
              </p>
            </div>
            <button 
              onClick={handleStartNewAudit}
              className="hidden md:inline-flex items-center px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-5 h-5 mr-2" />
              Start New Audit
            </button>
          </div>

          {/* Navigation Tabs */}
          <div className="hidden md:block border-b mb-8">
            <div className="flex justify-center gap-4 pb-4">
              {navItems.map((item) => (
                <NavButton 
                  key={item.id}
                  active={activeTab === item.id} 
                  onClick={() => setActiveTab(item.id)}
                  icon={item.icon}
                  count={item.count}
                >
                  {item.label}
                </NavButton>
              ))}
            </div>
          </div>
        </div>

        {/* Main Content */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
          </div>
        ) : (
          activeTab === 'Overview' ? renderOverviewTab() : (
            <div className="p-6 text-center text-gray-500">
              {activeTab} content coming soon...
            </div>
          )
        )}
      </div>

      {/* Mobile Navigation */}
      <MobileNavigation 
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        navItems={navItems}
      />

      {/* Mobile Floating Action Button */}
      <MobileFloatingButton onClick={handleStartNewAudit} />
    </div>
  );
};

export default AuditDashboard;